import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationService } from 'core/services/notification';
import { HttpService } from 'core/services/http';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Chip,
    IconButton,
    Divider,
    Stack,
    Badge,
    Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import MessageIcon from '@mui/icons-material/Message';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function NotificationsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [selectedNotification, setSelectedNotification] = useState(null);
    const user = useMemo(() => HttpService.getUser?.() ?? null, []);
    
    // Check if we're inside a dashboard layout or standalone
    const isInDashboard = location.pathname.includes('/employer/notifications') || 
                          location.pathname.includes('/jobseeker/notifications');
    const isEmployee = user?.role === 'employee';
    const isEmployer = user?.role === 'employer';

    const { data: notificationsData, isLoading, isError, refetch } = useQuery(
        'notifications',
        async () => {
            return await NotificationService.getNotifications();
        },
        {
            refetchInterval: 30000, // Refetch every 30 seconds
        }
    );

    const markAsReadMutation = useMutation(
        async (notificationId) => {
            return await NotificationService.markAsRead(notificationId);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('notifications');
                queryClient.invalidateQueries('unreadNotificationsCount');
            }
        }
    );

    const notifications = notificationsData?.results || notificationsData || [];
    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Redirect to appropriate dashboard notifications if accessing standalone route
    useEffect(() => {
        if (!isInDashboard && user) {
            if (isEmployee) {
                navigate('/jobseeker/notifications', { replace: true });
            } else if (isEmployer) {
                navigate('/employer/notifications', { replace: true });
            }
        }
    }, [isInDashboard, user, isEmployee, isEmployer, navigate]);

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            await markAsReadMutation.mutateAsync(notification.id);
        }

        // Navigate based on notification type and user role
        if (notification.type === 'application_status' && notification.type_id) {
            if (isEmployee) {
                navigate(`/jobseeker/applications/${notification.type_id}`);
            }
        } else if (notification.type === 'apply' && notification.type_id) {
            // For employers viewing applications
            if (isEmployer) {
                navigate(`/employer/applications/${notification.type_id}`);
            }
        } else if (notification.type === 'message' && notification.type_id) {
            // Navigate to messages if implemented
            // navigate(`/messages/${notification.type_id}`);
        }
    };

    const handleMarkAllAsRead = async () => {
        await markAsReadMutation.mutateAsync(null);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'application_status':
            case 'apply':
                return <WorkIcon />;
            case 'message':
                return <MessageIcon />;
            default:
                return <InfoIcon />;
        }
    };

    const getNotificationColor = (level) => {
        switch (level) {
            case 'success':
                return 'success';
            case 'warning':
                return 'warning';
            case 'error':
                return 'error';
            default:
                return 'info';
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                Failed to load notifications
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!isInDashboard && (
                        <IconButton onClick={() => navigate(-1)}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Typography variant="h4" fontWeight={700}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Chip
                            label={`${unreadCount} unread`}
                            color="primary"
                            size="small"
                        />
                    )}
                </Box>
                {unreadCount > 0 && (
                    <Button
                        variant="outlined"
                        startIcon={<CheckCircleOutlineIcon />}
                        onClick={handleMarkAllAsRead}
                        disabled={markAsReadMutation.isLoading}
                    >
                        Mark All as Read
                    </Button>
                )}
            </Box>

            <Paper sx={{ p: 2 }}>
                {notifications.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        You have no notifications yet.
                    </Alert>
                ) : (
                    <List>
                        {notifications.map((notification, index) => (
                            <React.Fragment key={notification.id}>
                                <ListItem
                                    disablePadding
                                    sx={{
                                        bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                                        '&:hover': {
                                            bgcolor: 'action.selected'
                                        }
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleNotificationClick(notification)}
                                        sx={{ py: 2 }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                                            <Box
                                                sx={{
                                                    color: getNotificationColor(notification.level) + '.main',
                                                    mt: 0.5
                                                }}
                                            >
                                                {getNotificationIcon(notification.type)}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={notification.is_read ? 400 : 600}
                                                        sx={{
                                                            flex: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {notification.title}
                                                    </Typography>
                                                    {!notification.is_read && (
                                                        <Chip
                                                            label="New"
                                                            color="primary"
                                                            size="small"
                                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 1,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {notification.description}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {new Date(notification.created_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ ml: 1 }}>
                                                {notification.is_read ? (
                                                    <CheckCircleIcon fontSize="small" color="action" />
                                                ) : (
                                                    <RadioButtonUncheckedIcon fontSize="small" color="primary" />
                                                )}
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                                {index < notifications.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    );
}

export default NotificationsPage;

